import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import loadable from '@loadable/component';
import queryString from 'querystring';
import { bemGenerator } from '@prisjakt/ui-base';
import { AppContext } from '@prisjakt/ui-app-context';
import Breadcrumb from '@prisjakt/ui-breadcrumb';
import T from '@prisjakt/ui-translate';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';
import Grid from '@prisjakt/ui-grid';
import ShopCard from '@prisjakt/ui-shop-card';

import PageContent from '../../components/page-content';
import PageHeader from '../../components/page-header';
import SotySearchNominees from './search-nominees';
import FeaturedArticle from '../landing-page/components/featured-article';

import './leaderboard.scss';

const SotyVotePopup = loadable(() =>
  import(/* webpackChunkName: "shop-of-the-year" */ './vote-popup')
);

const styles = bemGenerator('soty-leaderboard', [
  'badge',
  'charity',
  'charity-logo',
  'search',
  'content',
  'link',
  'current-vote',
  'legal',
]);

class ShopOfTheYearLeaderboard extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = { show_vote_popup: false };
  }

  /**
   * The Vote popup can also be opened through url parameters.
   * Example: /shop-of-the-year?category=2&shop=1751
   */
  handleOpenVoteFromUrl(props) {
    const { shopOfTheYear, store } = props.data;
    const qs = queryString.decode(props.location.search.substr(1));

    if (qs.category && qs.shop && store) {
      const categoryPopup = shopOfTheYear.categories.nodes.filter(
        item => item.id === parseInt(qs.category, 10)
      );

      if (categoryPopup.length > 0) {
        this.setState(() => ({
          show_vote_popup: {
            nominationCategory: categoryPopup[0],
            shop: store,
          },
        }));
      }
    }
  }

  componentDidMount() {
    this.handleOpenVoteFromUrl(this.props);
  }

  render() {
    const { t, market, linkTo } = this.context;
    const {
      year,
      leaderboards,
      charity,
      featuredArticle,
    } = this.props.data.shopOfTheYear;

    const { history: voteHistory } = this.props.soty_vote_popup;

    const cdnDir = `${market.cdn}/g/store_of_the_year/${year}/${market.code}`;
    const badgeURI = `${cdnDir}/award/main_award.png`;

    return (
      <div className={styles.block}>
        <PageContent withAds>
          <Fragment>
            <Breadcrumb
              homeText={t('common', 'breadcrumb.home')}
              path={[
                {
                  pathName: linkTo('shopOfTheYear'),
                  name: t('campaigns', 'shop-of-the-year.leaderboard-title'),
                  id: '1',
                },
              ]}
            />

            <PageHeader
              media={<img className={styles.badge} src={badgeURI} alt="" />}
              title={t('campaigns', 'shop-of-the-year.leaderboard-title')}
              right={
                charity && (
                  <div className={styles.charity}>
                    <img
                      className={styles['charity-logo']}
                      src={charity.logo}
                      alt={charity.name}
                    />
                    <p>{charity.description}</p>
                  </div>
                )
              }
            >
              <p>
                {t('campaigns', 'shop-of-the-year.leaderboard-description')}
              </p>
            </PageHeader>

            <div className={`column ${styles.search}`}>
              <SotySearchNominees year={year} />
            </div>

            {featuredArticle && (
              <FeaturedArticle
                {...featuredArticle}
                linkText={t('landing', 'featured-article.read-more')}
              />
            )}

            <div className={`column ${styles.content}`}>
              {leaderboards &&
                leaderboards.nodes &&
                leaderboards.nodes
                  // We don't want to list the main award (Shop of the Year) if it has `isSpecial`, because we don't vote for that award.
                  .filter(
                    category => !(category.mainAward && category.isSpecial)
                  )
                  .map(category => {
                    const currentVote = voteHistory.find(
                      vote => vote.category.id === category.id
                    );
                    return (
                      <Panel
                        key={category.id}
                        header={
                          <PanelHeader title={category.name}>
                            <Link
                              className={styles.link}
                              to={linkTo('shopOfTheYearCategory', {
                                year,
                                id: category.id,
                              })}
                            >
                              <T domain="campaigns">
                                shop-of-the-year.view-shops-in-category-link
                              </T>
                            </Link>
                          </PanelHeader>
                        }
                      >
                        <Grid
                          blocks={{
                            xs: 2,
                            sm: 2,
                            md: 3,
                            lg: 4,
                            xl: 4,
                          }}
                        >
                          {category.leaderboard &&
                            category.leaderboard &&
                            category.leaderboard.slice(0, 8).map(nominee => (
                              <ShopCard
                                key={nominee.id}
                                id={nominee.id}
                                name={nominee.name}
                                hasLogo={nominee.hasLogo}
                                width={176}
                                onClick={() =>
                                  this.setState({
                                    show_vote_popup: {
                                      nominationCategory: category,
                                      shop: nominee,
                                    },
                                  })
                                }
                                callToActionText={t(
                                  'campaigns',
                                  'shop-of-the-year.vote'
                                )}
                              />
                            ))}
                        </Grid>

                        {currentVote && (
                          <div className={styles['current-vote']}>
                            <T
                              domain="campaigns"
                              shopName={currentVote.store.name}
                              categoryName={category.name}
                            >
                              shop-of-the-year.voted-for-shop-in-category
                            </T>
                          </div>
                        )}
                      </Panel>
                    );
                  })}
              {this.state.show_vote_popup && (
                <SotyVotePopup
                  onRequestClose={() =>
                    this.setState({ show_vote_popup: false })
                  }
                  year={year}
                  nominationCategory={
                    this.state.show_vote_popup.nominationCategory
                  }
                  shop={this.state.show_vote_popup.shop}
                  charity={charity}
                />
              )}
              <div className={styles.legal}>
                <p>{t('campaigns', 'shop-of-the-year.legal-information')}</p>
              </div>
            </div>
          </Fragment>
        </PageContent>
      </div>
    );
  }
}

const { arrayOf, shape, string, number, bool } = PropTypes;

ShopOfTheYearLeaderboard.contextType = AppContext;

ShopOfTheYearLeaderboard.propTypes = {
  data: shape({
    year: number.isRequired,
    categories: shape({
      nodes: arrayOf(
        shape({
          id: number,
          name: string,
          isSpecial: bool,
          mainAward: bool,
          logo: string,
          nominees: shape({
            nodes: arrayOf(
              shape({
                id: number,
                name: string,
                logo: string,
                featured: bool,
              })
            ),
          }),
        })
      ),
    }),
    charity: shape({
      name: string,
      description: string,
      logo: string,
    }),
    shop: shape({
      id: number,
      name: string,
      featured: bool,
      logo: string,
    }),
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  soty_vote_popup: shape({
    history: arrayOf(
      shape({
        category: shape({
          id: number,
        }),
        store: shape({
          name: string,
        }),
      })
    ),
  }).isRequired,
};

export default connect(state => state.areas.soty)(
  withRouter(ShopOfTheYearLeaderboard)
);
